import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import ContentSpinner from '@/components/ContentSpinner';
import SearchBar from '@/components/SearchBar';
import Text from '@/components/Text';
import useDebounce from '@/hooks/useDebounce';
import { ReleaseResource } from '@/types/resources';
import { api } from '@/utils/api';
import { DistributionDepartmentsEnum, ReleaseStatusEnum } from '@/utils/enums';
import { hexToRgba } from '@/utils/theme';

import { PeriodTabs } from '..';
import DistributionListItem from './DistributionListItem';

const DateHolder = styled.div`
  background: ${({ theme }) => hexToRgba(theme.colors.black, 0.03)};
  border-radius: 2px;
  padding: 16px 24px;
  margin-bottom: 16px;
`;

const Group = styled.div`
  margin-bottom: 48px;
`;

export const NoShcedules = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  height: 177px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const NoSchedulesText = styled(Text)`
  color: ${({ theme }) => hexToRgba(theme.colors.gray, 0.6)};
  display: flex;
  align-items: center;
  svg {
    margin-right: 6px;
  }
`;

const buildListCalendar = (activeTab: PeriodTabs, reverse = false) => {
  const now = DateTime.now().startOf('day');

  const endDay = now.plus({ months: 1 });

  let startDay = now;

  const dayMonthBefore = now.minus({ months: 1 });

  const calendar = [];

  if (activeTab === PeriodTabs.FromToday) {
    while (startDay < endDay) {
      calendar.push(startDay);
      startDay = startDay.plus({ days: 1 });
    }
  } else {
    while (startDay > dayMonthBefore) {
      if (startDay !== now) {
        calendar.push(startDay);
      }
      startDay = startDay.minus({ days: 1 });
    }
    if (reverse) {
      calendar.reverse();
    }
  }

  return calendar;
};

interface Props {
  activeTab: PeriodTabs;
  activeStatus: ReleaseStatusEnum | null;
  activeDepartment: DistributionDepartmentsEnum;
  withRepostsOnly: boolean;
  selectedChannels: number[];
  activeType: null | 0 | 1;
  activeReserved: null | 0 | 1;
}

const DistributionList: FC<Props> = ({
  activeTab,
  activeStatus,
  activeDepartment,
  withRepostsOnly,
  selectedChannels,
  activeType,
  activeReserved,
}) => {
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 600);

  const filteredReleases = (day: DateTime) =>
    releases?.filter(
      (release) =>
        release.releasedAt &&
        DateTime.fromISO(release.releasedAt).toLocaleString() ===
          day.toLocaleString(),
    );

  const now = DateTime.now();

  const endDay = now.plus({ months: 1 });

  const dayMonthBefore = now.minus({ months: 1 });

  const calendar = buildListCalendar(activeTab);

  const distributionListQueryKey = [
    'releases',
    {
      // teamId,
      activeTab,
      selectedChannels,
      activeStatus,
      activeDepartment,
      withRepostsOnly,
      activeType,
      activeReserved,
      ...(!!debouncedSearch && {
        search: debouncedSearch,
      }),
    },
  ];

  const { data: releases, isLoading } = useQuery<ReleaseResource[]>(
    distributionListQueryKey,
    () =>
      api.get<never, ReleaseResource[]>(`/v1/releases`, {
        params: {
          // ...(teamId !== "all" && {
          //   "filter[teamId]": teamId,
          // }),
          ...(activeType !== null && {
            'filter[isReel]': activeType,
          }),
          ...(activeReserved !== null && {
            'filter[isReserved]': activeReserved,
          }),
          ...(activeDepartment !== DistributionDepartmentsEnum.ALL && {
            'filter[is3rdParty]':
              activeDepartment === DistributionDepartmentsEnum.NATIVE
                ? '0'
                : '1',
          }),
          ...(!!selectedChannels.length && {
            'filter[channelId]': selectedChannels.join(','),
          }),
          'filter[status]': [
            ReleaseStatusEnum.WAITING,
            ReleaseStatusEnum.SCHEDULED,
            ReleaseStatusEnum.POSTED,
            ReleaseStatusEnum.REJECTED,
          ].join(','),
          'filter[since]':
            activeTab === PeriodTabs.FromToday
              ? now.toISO()
              : dayMonthBefore.toISO(),
          'filter[until]':
            activeTab === PeriodTabs.FromToday ? endDay.toISO() : now.toISO(),
          ...(!!activeStatus && {
            'filter[status]': activeStatus,
          }),
          ...(withRepostsOnly && {
            'filter[with_reposts_only]': 1,
          }),
          ...(!!debouncedSearch && {
            'filter[search]': debouncedSearch,
          }),
        },
      }),
    {
      staleTime: 120 * 1000,
    },
  );

  if (isLoading) {
    return <ContentSpinner />;
  }

  return (
    <>
      {withRepostsOnly && (
        <SearchBar
          placeholder="Search by Project ID, Fresh ID"
          setValue={setSearch}
          value={search}
        />
      )}
      {calendar.map((day, index) => (
        <Group key={index}>
          <DateHolder>
            <Text $type="button">{day.toFormat('EEEE, d MMMM y')}</Text>
          </DateHolder>
          {!!filteredReleases(day)?.length ? (
            filteredReleases(day)?.map((release) => (
              <DistributionListItem
                key={release.id}
                release={release}
                showRepostsDetails={withRepostsOnly}
              />
            ))
          ) : (
            <NoShcedules>
              <NoSchedulesText $type="caption">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.4616 4.42857H5.53854C5.07122 4.42857 4.69238 4.81233 4.69238 5.28572V22.4286C4.69238 22.902 5.07122 23.2857 5.53854 23.2857H22.4616C22.9289 23.2857 23.3078 22.902 23.3078 22.4286V5.28572C23.3078 4.81233 22.9289 4.42857 22.4616 4.42857Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19.0767 2.71428V6.14285"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8.92285 2.71428V6.14285"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.69238 9.57143H23.3078"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                No schedules
              </NoSchedulesText>
            </NoShcedules>
          )}
        </Group>
      ))}
    </>
  );
};

export default DistributionList;
